import { HeroId } from "../../../../shared/src/index.ts";
import type { VoiceScript } from "../voice-types.ts";

const script = {
  hero: HeroId.IonMan,
  voices: {
    main: {
      description:
        "confident armored engineer baritone, fast witty delivery, premium helmet intercom compression, bright top-end radio polish, charismatic battlefield swagger",
      seed: 10469,
      guidance: 0.76,
      loudness: 0.78,
      fx: { eq: "helmet" },
    },
  },
  lines: {
    select: [
      {
        id: "select_1",
        text: "I never wanted to be Ion Man, but it worked out for everybody.",
      },
      { id: "select_2", text: "Ion Man standing by." },
      { id: "select_3", text: "What's a team of heroes without Ion Man?" },
      { id: "select_4", text: "Every team needs Ion Man." },
      {
        id: "select_5",
        text: "Cap's the boss. I just pay for everything, and design everything, and make everyone look cooler.",
      },
    ],
    spawn: [
      { id: "spawn_1", text: "Gamma powered and ready to go!" },
      { id: "spawn_2", text: "All right! Science bros, unite!" },
      { id: "spawn_3", text: "I went electric before it was cool…" },
      {
        id: "spawn_4",
        text: "You and me, Goldilocks. Let's bring the thunder!",
      },
      {
        id: "spawn_5",
        text: "Rebuild the space-time continuum and defeat two Doctor Dooms? Piece of cake.",
      },
      {
        id: "spawn_6",
        text: "If I could get a look at Doom's machine, I could solve this problem by lunchtime.",
      },
      {
        id: "spawn_7",
        text: "Some people are natural heroes. I'm a natural joker who is occasionally heroic.",
      },
      {
        id: "spawn_8",
        text: "Alright, somebody tell me this isn't some kid's birthday party again.",
      },
      { id: "spawn_9", text: "Sorry I'm late. Had to buy a company." },
      {
        id: "spawn_10",
        text: "Underneath my caustic exterior, I'm…still pretty caustic.",
      },
      {
        id: "spawn_11",
        text: "Don't bother trying to make people care. Show them what matters to you.",
      },
      {
        id: "spawn_12",
        text: "People want to be led, I want to be in charge. Win-win.",
      },
      {
        id: "spawn_13",
        text: "Guess we better save the world again. Or is it worlds this time?",
      },
      {
        id: "spawn_14",
        text: "If you were as smart as me, you'd be arrogant too.",
      },
      {
        id: "spawn_15",
        text: "If I'm not joking about something, you know I'm not taking it seriously.",
      },
      {
        id: "spawn_16",
        text: "I never had kids, but I did have the Avengers. Same thing.",
      },
      {
        id: "spawn_17",
        text: "I won't shy away from a fight, but I'd rather be in the lab.",
      },
      {
        id: "spawn_18",
        text: "Trudy's going to get all this sorted out. All I have to do is build her.",
      },
      {
        id: "spawn_19",
        text: "We don't have time for a city hall debate. Let's go!",
      },
      {
        id: "spawn_20",
        text: "How did everything go so wrong, F.R.I.D.A.Y.? I really blew it.",
      },
      { id: "spawn_21", text: "Peace in our time. Imagine that." },
      { id: "spawn_22", text: "Smell the silent judgment?" },
      {
        id: "spawn_23",
        text: "Anybody remember when I carried a nuke through a wormhole? Saved New York?",
      },
      {
        id: "spawn_24",
        text: "You know Ultron is trying to tear us apart, right?",
      },
      {
        id: "spawn_25",
        text: "Never been one to shrink from an honest challenge.",
      },
    ],
    respawn: [
      { id: "respawn_1", text: "I'm gonna find whoever put me out last time." },
      { id: "respawn_2", text: "I'm back." },
      { id: "respawn_3", text: "Ion Man operational again." },
      { id: "respawn_4", text: "Everybody ready?" },
    ],
    ult_ally: [{ id: "ult_ally", text: "Pulse charge…fire!" }],
    ult_enemy: [{ id: "ult_enemy", text: "Maximum pulse!" }],
    ability2: [
      { id: "ability2_1", text: "Powering up." },
      { id: "ability2_2", text: "It's nano time." },
      { id: "ability2_3", text: "Nano me up!" },
    ],
    ability3: [
      { id: "ability3_1", text: "Haha! Look out below…" },
      { id: "ability3_2", text: "Bombs away!" },
      { id: "ability3_3", text: "Anyone say fireworks?" },
      { id: "ability3_4", text: "Rockets…fire!" },
      { id: "ability3_5", text: "Targets locked. Let’s do this!" },
      { id: "ability3_6", text: "F.R.I.D.A.Y., lock and load." },
      { id: "ability3_7", text: "Multiple targets identified. Fire!" },
    ],
    kill: [
      { id: "kill_1", text: "I hope F.R.I.D.A.Y. got that on video." },
      { id: "kill_2", text: "There's another one down." },
      { id: "kill_3", text: "What do I hit next? I'm taking requests." },
      { id: "kill_4", text: "Piece of cake." },
      { id: "kill_5", text: "One step closer to saving the world." },
    ],
    multikill: [
      { id: "multikill_1", text: "I'm not stopping now!" },
      { id: "multikill_2", text: "Going for four." },
      { id: "multikill_3", text: "Three the hard way." },
      { id: "multikill_4", text: "Anybody else got five? Didn't think so." },
      { id: "multikill_5", text: "I'm high-fiving myself here." },
      { id: "multikill_6", text: "Take five! I mean, I already did." },
      { id: "multikill_7", text: "That's it? That's all you got?" },
      { id: "multikill_8", text: "Oh jeez, I have to do everything myself." },
      {
        id: "multikill_9",
        text: "Oh, did the rest of you enjoy your little coffee break?",
      },
    ],
    low_hp: [
      { id: "low_hp_1", text: "Crisis averted." },
      { id: "low_hp_2", text: "Last-minute miracle!" },
    ],
    healed: [
      { id: "healed_1", text: "Ah, that's better." },
      { id: "healed_2", text: "That put a spring in my step." },
      { id: "healed_3", text: "Uh, feeling good again." },
      { id: "healed_4", text: "Uh, I needed that." },
    ],
  },
} satisfies VoiceScript;

export default script;
